import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';

import recipesService from '../../service/api/RecipesService';
import ordersService from '../../service/api/OrdersService';
import OrderDialog from './OrderDialog';
import CreateOrderDialog from './CreateOrderDialog';
import { useTranslation } from 'react-i18next';
import { Order, OrderStatus } from '../../types/Order';

import { Toast } from 'primereact/toast';
import { SETTINGS } from '../../enums/settings.enum';

import { Recipe } from '../../types/Recipe';
import { addDays, format, parseISO } from 'date-fns';
import getPaginatorTemplate from '../_shared/getPaginatorTemplate';
import { deepCopy } from '../_shared/objectHelpers';
import { Calendar, CalendarChangeParams } from 'primereact/calendar';
import WriteOnlyButton from '../controls/WriteOnlyButton';
import scalesService from '../../service/api/ScalesService';
import { MultiSelect } from 'primereact/multiselect';
import { Scale } from '../ScalesIndications/types';
import groupsService from '../../service/api/GroupsService';
import { GroupConnectors } from '../../types/GroupConnectors';

export type OrdersFilter = {
  recipe?: string[];
  scale?: string[];
  status?: string[];
};

export const OrderManagement = () => {
  const toast = useRef<any>();

  const [loading, setLoading] = useState(true);

  const [orders, setOrders] = useState<Order[]>(null);

  const [recipes, setRecipes] = useState<Recipe[]>();

  const [scales, setScales] = useState<Scale[]>();

  const [groupConnectors, setGroupConnectors] = useState<GroupConnectors[]>();

  const [selectedRowToEdit, setSelectedRowToEdit] = useState<Order>(null);
  const [viewDetailsDialogVisible, setViewDetailsDialogVisible] = useState(false);
  const [addDialogVisible, setAddDialogVisible] = useState(false);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const { t } = useTranslation();
  const [startRecipeId, setStartRecipeId] = useState<string>(null);

  const emptyPaginatorState = {
    currentPage: 1,
    totalPages: 1,
    rows: 10,
  };

  const emptyPaginationParameters = {
    order: {
      isAscending: false,
      orderColumn: '',
    },
    page: {
      index: 1,
      size: 10,
    },
  };

  const [filters, setFilters] = useState<OrdersFilter>({});

  const [paginatorState, setPaginatorState] = useState(emptyPaginatorState);

  const [paginationParameters, setPaginationParameters] = useState(emptyPaginationParameters);

  const handlePageChange = (pageNumber) => {
    setPaginatorState((prevState) => ({ ...prevState, currentPage: pageNumber }));
    setPaginationParameters((prevState) => {
      const clone = deepCopy(prevState);
      clone.page.index = pageNumber;
      return clone;
    });
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    setPaginatorState((prevState) => ({ ...prevState, currentPage: 1, rows: rowsPerPage }));
    setPaginationParameters((prevState) => {
      const clone = deepCopy(prevState);
      clone.page.index = 1;
      clone.page.size = rowsPerPage;
      return clone;
    });
  };

  const showAddToast = (summary: string) => {
    toast.current.show({
      severity: 'success',
      summary: summary,
      detail: t('orders.toast_added'),
      life: SETTINGS.TOAST_LIFETIME,
    });
  };

  const loadData = async () => {
    setLoading(true);

    const ordersResponse = await ordersService.search(
      paginationParameters,
      {
        startDate: startDate ? format(startDate, SETTINGS.API_DATE_FORMAT) : null,
        endDate: endDate ? format(addDays(endDate, 1), SETTINGS.API_DATE_FORMAT) : null,
      },
      filters
    );
    const recipes = await recipesService.getAll();

    setRecipes(recipes);
    const recipesDict = Object.assign({}, ...recipes.map((r) => ({ [r.id]: r.name })));

    const scales = await scalesService.get();
    setScales(scales);
    const scalesDict = Object.assign({}, ...scales.map((r) => ({ [r.id]: r.name })));

    setPaginatorState((prevStat) => {
      return {
        ...prevStat,
        totalPages: ordersResponse.totalPageNumber,
        currentPage: prevStat.currentPage > ordersResponse.totalPageNumber ? 1 : prevStat.currentPage,
      };
    });
    const orders = ordersResponse.data;

    orders.forEach((_x) => {
      _x.recipeName = recipesDict[_x.recipeId];
      _x.scaleName = scalesDict[_x.scaleId];
    });
    setOrders(orders);

    const groupsResponse = await groupsService.getAll();
    setGroupConnectors(groupsResponse.flatMap((g) => g.connectors));

    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationParameters, filters]);

  const ordersTableHeader = (
    <div className="table-header">
      {t('orders.header')}
      <span className="p-input-icon-left">
        <Calendar
          placeholder={t('common.date_from')}
          locale="pl"
          inputId="startDate"
          value={startDate}
          className={`mr-1`}
          onChange={(element: CalendarChangeParams) => {
            setStartDate(element?.value as Date);
            handlePageChange(1);
          }}
          showIcon
        />

        <Calendar
          placeholder={t('common.date_to')}
          locale="pl"
          inputId="endDate"
          value={endDate}
          className={`mr-1`}
          onChange={(element: CalendarChangeParams) => {
            setEndDate(element?.value as Date);
            handlePageChange(1);
          }}
          showIcon
        />
      </span>
    </div>
  );

  const closeAddModal = () => {
    setAddDialogVisible(false);
    loadData();
  };

  const closeDetailsModal = () => {
    setViewDetailsDialogVisible(false);
    loadData();
  };

  const viewOrder = async (_order: Order) => {
    const order = await ordersService.get(_order.id); // we need to get all the details, including ingredients
    setSelectedRowToEdit(order);
    setViewDetailsDialogVisible(true);
  };

  const addOrder = (recipeId: string) => {
    setStartRecipeId(recipeId);
    setAddDialogVisible(true);
  };

  const resumeOrder = async (orderId: string) => {
    await ordersService.resume(orderId);
    loadData();
  };

  const pauseOrder = async (orderId: string) => {
    await ordersService.pause(orderId);
    loadData();
  };

  const stopOrder = async (orderId: string) => {
    await ordersService.stop(orderId);
    loadData();
  };

  const bodyTemplate = (data: Order, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  const statusTemplate = (data: Order, props: ColumnBodyOptions) => {
    return <>{t(`orders.statuses.${data[props.field]}`)}</>;
  };

  const dateTemplate = (data: Order, props: ColumnBodyOptions): JSX.Element => {
    const field = data[props.field];
    if (!field) {
      return <></>;
    }

    return <>{format(parseISO(field), t('formats.full_format'))}</>;
  };

  const actionTemplate = (data: Order) => (
    <span>
      {[OrderStatus.FINISHED, OrderStatus.CANCELLED].includes(data.status) ? (
        <>
          <Button
            type="button"
            icon="pi pi-eye"
            tooltip={t('common.preview')}
            tooltipOptions={{ position: 'top' }}
            className="p-button-success mr-2"
            onClick={() => {
              viewOrder(data);
            }}
          ></Button>
          <WriteOnlyButton
            type="button"
            icon="pi pi-refresh"
            tooltip={t('common.redo')}
            tooltipOptions={{ position: 'top' }}
            className="p-button-success mr-2"
            onClick={() => {
              addOrder(data.recipeId);
            }}
          />
        </>
      ) : (
        <></>
      )}
      {data.status === OrderStatus.PAUSED ? (
        <WriteOnlyButton
          type="button"
          icon="pi pi-play"
          tooltip={t('common.resume')}
          tooltipOptions={{ position: 'top' }}
          className="p-button-success mr-2"
          onClick={() => {
            resumeOrder(data.id);
          }}
        />
      ) : (
        <></>
      )}
      {data.status === OrderStatus.IN_PROGRESS ? (
        <WriteOnlyButton
          type="button"
          icon="pi pi-pause"
          tooltip={t('common.pause')}
          tooltipOptions={{ position: 'top' }}
          className="p-button-success mr-2"
          onClick={() => {
            pauseOrder(data.id);
          }}
        />
      ) : (
        <></>
      )}
      {[OrderStatus.IN_PROGRESS, OrderStatus.PAUSED].includes(data.status) ? (
        <WriteOnlyButton
          type="button"
          icon="pi pi-stop"
          tooltip={t('common.stop')}
          tooltipOptions={{ position: 'top' }}
          className="p-button-success mr-2"
          onClick={() => {
            stopOrder(data.id);
          }}
        />
      ) : (
        <></>
      )}
    </span>
  );

  const recipesFilterTemplate = () => {
    return (
      <MultiSelect
        value={filters.recipe}
        options={recipes?.map((r) => {
          return { label: r.name, value: r.id };
        })}
        onChange={(e) => setFilters({ ...filters, recipe: e.value.length ? e.value : undefined })}
        placeholder={t('common.filter')}
        className="p-column-filter"
        maxSelectedLabels={1}
        selectedItemsLabel={`${t('common.selected_label')}`}
        style={{ minWidth: '14rem' }}
      />
    );
  };

  const scalesFilterTemplate = () => {
    return (
      <MultiSelect
        value={filters.scale}
        options={scales?.map((r) => {
          return { label: r.name, value: r.id };
        })}
        onChange={(e) => setFilters({ ...filters, scale: e.value.length ? e.value : undefined })}
        placeholder={t('common.filter')}
        className="p-column-filter"
        maxSelectedLabels={1}
        selectedItemsLabel={`${t('common.selected_label')}`}
        style={{ minWidth: '14rem' }}
      />
    );
  };

  const statusFilterTemplate = () => {
    return (
      <MultiSelect
        value={filters.status}
        options={Object.keys(OrderStatus).map((s) => {
          return { label: t(`orders.statuses.${s}`), value: s };
        })}
        onChange={(e) => setFilters({ ...filters, status: e.value.length ? e.value : undefined })}
        placeholder={t('common.filter')}
        className="p-column-filter"
        maxSelectedLabels={1}
        selectedItemsLabel={`${t('common.selected_label')}`}
        style={{ minWidth: '10rem' }}
      />
    );
  };

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={orders}
            paginator
            className="p-datatable-customers"
            rows={paginatorState.rows}
            dataKey="id"
            rowHover
            emptyMessage={t('orders.empty_message')}
            loading={loading}
            header={ordersTableHeader}
            filterDisplay="row"
            paginatorTemplate={getPaginatorTemplate(paginatorState, handlePageChange, handleRowsPerPageChange, null)}
          >
            <Column field="name" header={t('common.name')} body={bodyTemplate}></Column>
            <Column
              field="recipeName"
              header={t('orders.recipe')}
              filter
              showFilterMenu={false}
              filterElement={recipesFilterTemplate}
              body={bodyTemplate}
            ></Column>
            <Column
              field="scaleName"
              header={t('orders.scale')}
              filter
              showFilterMenu={false}
              filterElement={scalesFilterTemplate}
              body={bodyTemplate}
            ></Column>
            <Column
              field="status"
              header={t('orders.status')}
              filter
              showFilterMenu={false}
              filterElement={statusFilterTemplate}
              body={statusTemplate}
            ></Column>
            <Column field="startDate" header={t('orders.start_date')} body={dateTemplate}></Column>
            <Column field="endDate" header={t('orders.end_date')} body={dateTemplate}></Column>
            <Column
              headerStyle={{ width: '8rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
            ></Column>
          </DataTable>
          <OrderDialog
            dialogVisible={viewDetailsDialogVisible}
            closeAddEditModal={closeDetailsModal}
            order={selectedRowToEdit}
            availableRecipes={recipes}
            availableConnectors={groupConnectors}
          />
          <CreateOrderDialog
            dialogVisible={addDialogVisible}
            closeAddEditModal={closeAddModal}
            recipeId={startRecipeId}
            showAddToast={showAddToast}
          />
        </div>
      </div>

      <Toast ref={toast} />
    </div>
  );
};
